/* istanbul ignore file */

import { Module } from 'vuex';
import { AppState } from '..';
import { prefixValues } from '@/helpers/util';
import { OrderConfirmationCreateAccountExperience, SelectableVariantMenuItem, TopLevelMenuGroup, UpsellItem } from '@/models';
import { AdobeTargetResponsePersonalizationPayload, MealBuilderCustomizationsResponse, MealBuilderExperienceResponse, MealBuilderItemDetails, TopLevelMenuImageContent, UtmParameters } from '@/models/adobe-target';

export interface AdobeTargetModuleState {
  reorderedTopLevelMenu: TopLevelMenuGroup[] | null;
  utmParameters: UtmParameters | null;
  isFeaturedSectionAboveTopLevelMenu: boolean;
  mealBuilderProteinSortOrder: SelectableVariantMenuItem[];
  mealBuilderItemDetails: MealBuilderItemDetails[] | null;
  ecid: string;
  region: string;
  orderConfirmationCreateAccountExperience: OrderConfirmationCreateAccountExperience | null;
  personalizedUpsellCmgIds: string[];
  personalizedUpsellTitle: string;
  displayedUpsellItems: UpsellItem[];
  propositionDisplayData: Map<string, AdobeTargetResponsePersonalizationPayload | null>;
  topLevelMenuImages: TopLevelMenuImageContent[];
  mealBuilderMobileCustomizationsExperienceEnabled: MealBuilderCustomizationsResponse;
  mealBuilderMobileCustomizationsExperienceData: MealBuilderExperienceResponse | null;
  loyaltyAutoAccrualEnabled: boolean;
}

const modulePrefix = 'adobeTarget/';
const MUTATION_NAMES = {
  SET_TOP_LEVEL_MENU_REORDERING: 'setReorderedTopLevelMenu',
  CLEAR_TOP_LEVEL_MENU_REORDERING: 'clearReorderedTopLevelMenu',
  SET_UTM_PARAMETERS: 'setUtmParameters',
  SET_FEATURED_SECTION_ABOVE_TOP_LEVEL_MENU: 'setFeaturedSectionAboveTopLevelMenu',
  SET_MEAL_BUILDER_PROTEIN_SORT_ORDER: 'setfMealBuilderProteinSortOrder',
  SET_MEAL_BUILDER_ITEM_DETAILS: 'setMealBuilderItemDetails',
  SET_CUSTOMER_ECID: 'setCustomerECID',
  SET_CUSTOMER_REGION: 'setCustomerRegion',
  SET_ORDER_CONFIRMATION_CREATE_ACCOUNT_EXPEREINCE: 'setOrderConfirmationCreateAccountExperience',
  SET_PERSONALIZED_UPSELL_CMG_IDS: 'setPersonalizedUpsellCmgIds',
  SET_PERSONALIZED_UPSELL_TITLE: 'setPersonalizedUpsellTitle',
  SET_DISPLAYED_UPSELL_ITEMS: 'setDisplayedUpsellItems',
  SET_PROPOSITION_DISPLAY_DATA: 'setPropositionDisplayData',
  SET_TOP_LEVEL_MENU_IMAGES: 'setTopLevelMenuImages',
  CLEAR_TOP_LEVEL_MENU_IMAGES: 'clearTopLevelMenuImages',
  SET_MEAL_BUILDER_MOBILE_CUSTOMIZATIONS_EXPERIENCE_ENABLED: 'setMealBuilderMobileCustomizationsExperienceEnabled',
  SET_MEAL_BUILDER_MOBILE_METADATA: 'setMealBuilderMobileMetadata',
  SET_LOYALTY_AUTO_ACCRUAL_ENABLED: 'setLoyaltyAutoAccrualEnabled'
};

export const MUTATIONS = prefixValues(MUTATION_NAMES, modulePrefix);

export function makeAdobeTargetModule(): Module<AdobeTargetModuleState, AppState> {
  return {
    namespaced: true,
    state: {
      reorderedTopLevelMenu: null,
      utmParameters: null,
      isFeaturedSectionAboveTopLevelMenu: false,
      mealBuilderProteinSortOrder: [],
      mealBuilderItemDetails: [],
      ecid: '',
      region: '',
      orderConfirmationCreateAccountExperience: null,
      personalizedUpsellCmgIds: [],
      personalizedUpsellTitle: '',
      displayedUpsellItems: [],
      propositionDisplayData: new Map<string, AdobeTargetResponsePersonalizationPayload>(),
      topLevelMenuImages: [],
      mealBuilderMobileCustomizationsExperienceEnabled: MealBuilderCustomizationsResponse.NotSet,
      mealBuilderMobileCustomizationsExperienceData: null,
      loyaltyAutoAccrualEnabled: false
    },
    mutations: {
      [MUTATION_NAMES.SET_TOP_LEVEL_MENU_REORDERING]: (state, reorderedTopLevelMenu: TopLevelMenuGroup[]) => {
        state.reorderedTopLevelMenu = reorderedTopLevelMenu;
      },
      [MUTATION_NAMES.CLEAR_TOP_LEVEL_MENU_REORDERING]: (state) => {
        state.reorderedTopLevelMenu = null;
      },
      [MUTATION_NAMES.SET_UTM_PARAMETERS]: (state, utmParameters: UtmParameters) => {
        state.utmParameters = utmParameters;
      },
      [MUTATION_NAMES.SET_FEATURED_SECTION_ABOVE_TOP_LEVEL_MENU]: (state, isFeaturedSectionAboveTopLevelMenu: boolean) => {
        state.isFeaturedSectionAboveTopLevelMenu = isFeaturedSectionAboveTopLevelMenu;
      },
      [MUTATION_NAMES.SET_MEAL_BUILDER_PROTEIN_SORT_ORDER]: (state, mealBuilderProteinsSortOrder: SelectableVariantMenuItem[]) => {
        state.mealBuilderProteinSortOrder = mealBuilderProteinsSortOrder;
      },
      [MUTATION_NAMES.SET_MEAL_BUILDER_ITEM_DETAILS]: (state, mealBuilderItemDetails: MealBuilderItemDetails[]) => {
        state.mealBuilderItemDetails = mealBuilderItemDetails;
      },
      [MUTATION_NAMES.SET_CUSTOMER_ECID]: (state, ecid: string) => {
        state.ecid = ecid;
      },
      [MUTATION_NAMES.SET_CUSTOMER_REGION]: (state, region: string) => {
        state.region = region;
      },
      [MUTATION_NAMES.SET_ORDER_CONFIRMATION_CREATE_ACCOUNT_EXPEREINCE]: (state, orderConfirmationCreateAccountExperience: OrderConfirmationCreateAccountExperience) => {
        state.orderConfirmationCreateAccountExperience = orderConfirmationCreateAccountExperience;
      },
      [MUTATION_NAMES.SET_PERSONALIZED_UPSELL_CMG_IDS]: (state, personalizedUpsellCmgIds: string[]) => {
        state.personalizedUpsellCmgIds = personalizedUpsellCmgIds;
      },
      [MUTATION_NAMES.SET_PERSONALIZED_UPSELL_TITLE]: (state, personalizedUpsellTitle: string) => {
        state.personalizedUpsellTitle = personalizedUpsellTitle;
      },
      [MUTATION_NAMES.SET_DISPLAYED_UPSELL_ITEMS]: (state, displayedUpsellItems: UpsellItem[]) => {
        state.displayedUpsellItems = displayedUpsellItems;
      },
      [MUTATION_NAMES.SET_PROPOSITION_DISPLAY_DATA]: (state, propositionDisplayData: AdobeTargetResponsePersonalizationPayload | null) => {
        if (propositionDisplayData?.scope) {
          state.propositionDisplayData.set(propositionDisplayData.scope, propositionDisplayData);
        }
      },
      [MUTATION_NAMES.SET_TOP_LEVEL_MENU_IMAGES]: (state, topLevelMenuImageContent: TopLevelMenuImageContent[]) => {
        state.topLevelMenuImages = topLevelMenuImageContent;
      },
      [MUTATION_NAMES.CLEAR_TOP_LEVEL_MENU_IMAGES]: (state) => {
        state.topLevelMenuImages = [];
      },
      [MUTATION_NAMES.SET_MEAL_BUILDER_MOBILE_CUSTOMIZATIONS_EXPERIENCE_ENABLED]: (state, enabled: MealBuilderCustomizationsResponse) => {
        state.mealBuilderMobileCustomizationsExperienceEnabled = enabled;
      },
      [MUTATION_NAMES.SET_MEAL_BUILDER_MOBILE_METADATA]: (state, metadata: MealBuilderExperienceResponse) => {
        state.mealBuilderMobileCustomizationsExperienceData = metadata
      },
      [MUTATION_NAMES.SET_LOYALTY_AUTO_ACCRUAL_ENABLED]: (state, enabled: boolean) => {
        state.loyaltyAutoAccrualEnabled = enabled;
      }
    },
  };
}
